"use client";

import { useState, useRef } from "react";

import {
  userConsentVariant,
  type UserConsentVariantProps,
} from "./user-consent.variants";
import { useApp } from "@/hooks/use-app";
import { UserConsentType } from "@/types/strapi/content";

export type UseUserConsentProps = UserConsentVariantProps & {
  className?: string;
  style?: Record<string, string | number>;
  data: UserConsentType;
};

export const useUserConsent = (props: UseUserConsentProps) => {
  const { className = "", data, ...componentProps } = props;
  const { state, onConsentModalOpenChange, onAcceptedConsent } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);
  const [sendSubmit, setSendSubmit] = useState(false);
  const styles = userConsentVariant();
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const handleConsent = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();

      const formData = new FormData(e.target as HTMLFormElement);
      const name = formData.get("name") as string;
      const email = formData.get("email") as string;
      const phone = formData.get("phone") as string;
      const acceptedTerms = formData.get("accepted-terms") as string;

      if (!sendSubmit) setSendSubmit(true);

      if (!name) {
        nameRef.current?.focus();
        return;
      }

      if (!email) {
        emailRef.current?.focus();
        return;
      }

      if (!acceptedTerms) {
        setCheckboxError(true);
        return;
      }

      setIsLoading(true);

      const response = await fetch("/api/user-consent", {
        method: "POST",
        body: JSON.stringify({ name, email, phone, resourceId: state.assetId ?? null }),
      });

      setIsLoading(false);

      if (!response.ok) {
        alert("Failed to save user consent. Please try again.");
        return;
      }

      const data = await response.json();

      if (data.data.success) {
        onAcceptedConsent(email, name);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    onConsentModalOpenChange(open, null);
  };

  return {
    className,
    styles,
    componentProps,
    open: state.consentModalOpen,
    onOpenChange: handleOpenChange,
    onSubmit: handleConsent,
    isLoading,
    nameRef,
    emailRef,
    data,
    checkboxError,
    setCheckboxError,
    errorClasses: sendSubmit
      ? "focus:invalid:border-feedback-error-9 focus:invalid:ring-feedback-error-9"
      : "",
  };
};
